import Phaser from 'phaser';

import { BoardViewModel } from './board-view-model';
import { SolitaireMainScene } from '../../scenes/solitaire-main-scene';
import { DeckPileView } from './deck-pile-view';
import { WastePileView } from './waste-pile-view';
import { FoundationPileView } from './foundation-pile-view';
import { TableauPileView } from './tableau-pile-view';
import { Subscription } from 'rxjs';
import { CardView } from '../card/card-view';
import { StartView } from './start-view';
import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { ResizeMetrics } from '../../resize-metrics';

export class BoardView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;

  private subscriptionGroup: Subscription;

  private tableauPiles: TableauPileView[];
  private foundationPiles: FoundationPileView[];
  private wastePile: WastePileView;
  private deckPile: DeckPileView;
  private cardViews: CardView[] = [];
  private startView: StartView;

  constructor(
    scene: SolitaireMainScene,
    x: number,
    y: number,
    private viewModel: BoardViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.tableauPiles = viewModel.tableausPileViewModels.map(
      (vm) => new TableauPileView(scene, 0, 0, vm),
    );
    this.foundationPiles = viewModel.foundationPileViewModels.map(
      (vm) => new FoundationPileView(scene, 0, 0, vm),
    );
    this.wastePile = new WastePileView(scene, 0, 0, viewModel.wastePileViewModel);
    this.deckPile = new DeckPileView(scene, 0, 0, viewModel.deckPileViewModel);

    this.add([...this.tableauPiles, ...this.foundationPiles, this.wastePile, this.deckPile]);

    this.startView = new StartView(scene, 0, 0, viewModel.startViewModel);
    this.add(this.startView);

    this.setName(BoardView.name);
    scene.add.existing(this);

    scene.events.on(
      Phaser.Scenes.Events.UPDATE,
      (time: number, delta: number) => this.sceneUpdate(time, delta),
      this,
    );

    this.subscriptionGroup.add(
      viewModel.onCardDragStartTrigger?.subscribe((value) => {
        this.cardViews?.forEach((view) => view.handleCardDragStart(value));
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onCardDragTrigger?.subscribe((value) => {
        this.cardViews?.forEach((view) => view.handleCardDrag(value));
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onCardDragEndTrigger?.subscribe((value) => {
        this.cardViews?.forEach((view) => view.handleCardDragEnd(value));
        this.tableauPiles?.forEach((view) => view.handleCardDragEnd(value));
        this.foundationPiles?.forEach((view) => view.handleCardDragEnd(value));
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onNewDealStartedTrigger.subscribe((_value) => {
        if (viewModel.spawnDeck()) {
          this.cardViews = viewModel.allCardViewModels.map((vm) => new CardView(scene, 0, 0, vm));
        }
        const resizeMetrics = this.currentResizeMetrics;
        if (resizeMetrics) {
          this.cardViews.forEach((view) => view.onResize(resizeMetrics));
        }
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onPossibleHintsTrigger?.subscribe((value) => {
        viewModel.handleHintShake(value);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onClearBoard$.subscribe((_value) => {
        this.cardViews.forEach((view) => {
          view.destroy();
        });
        this.cardViews.length = 0;
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onBoardChangedTrigger?.subscribe((_value) => {
        viewModel.boardChanged();
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onGameStateChangedTrigger?.subscribe((value) => {
        void viewModel.gameStateChanged(value);
      }),
    );
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    const scalingMetrics = this.viewModel.calculateScaleFactor(gameSizeMetrics);

    const resizeMetrics = {
      layoutConfig: this.viewModel.layoutConfig,
      sizeMetrics: gameSizeMetrics,
      scalingMetrics: scalingMetrics,
    };
    this.currentResizeMetrics = resizeMetrics;

    this.tableauPiles.forEach((view) => view.onResize(resizeMetrics));
    this.foundationPiles.forEach((view) => view.onResize(resizeMetrics));
    this.wastePile.onResize(resizeMetrics);
    this.deckPile.onResize(resizeMetrics);
    this.cardViews.forEach((view) => view.onResize(resizeMetrics));
    this.startView?.onResize(resizeMetrics);
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  private sceneUpdate(time: number, delta: number) {
    this.cardViews.forEach((view) => {
      view.sceneUpdate(time, delta);
    });
  }
}
